import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, ArrowRight, ArrowLeft, Check, Loader2, Calendar, Flag, Sparkles, MousePointerClick, Trash2 } from 'lucide-react';
import API_URL from '../config';
import { useUser } from '../context/UserContext';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';


const GoalWizard = ({ isOpen, onClose, onComplete }) => {
  const { user } = useUser();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    mission: '',
    vision: ['', '', ''],
    processGoals: ['', '', '']
  });
  const [suggestions, setSuggestions] = useState({
    mission: '',
    vision: [],
    processGoals: []
  });

  const steps = [
    { title: 'Introduction', field: 'intro', description: 'MVP - What is it and Why you should have one', instruction: '' },
    { title: 'Mission Statement', field: 'mission', description: 'Define your purpose and how you will achieve your vision', instruction: '' },
    { title: 'Vision', field: 'vision', description: 'What do you want to achieve this year or season?', instruction: 'Set 1 to 3 goals for this year or season that align with your mission.' },
    { title: 'Process Goals', field: 'processGoals', description: 'What daily/weekly actions will help you achieve your goals?', instruction: 'Set 1 to 3 process goals that outline the daily/weekly actions needed to achieve your vision.' },
    { title: 'Review & Confirm', field: 'review', description: 'Review and confirm your goals', instruction: '' }
  ];

  const currentStep = steps[step];

  useEffect(() => {
    if (isOpen && user) {
      Mixpanel.trackFeatureUsed('GoalWizard', 'Opened');
    }
  }, [isOpen, user]);

  const handleInputChange = (field, value, index = null) => {
    // Handle both event objects and direct values
    if (value && value.target) {
      // If this is an event object
      value = value.target.value;
    }
    
    if (index !== null) {
      // Handle array fields
      setFormData(prev => {
        const newArray = [...(prev[field] || [])];
        newArray[index] = value;
        return { ...prev, [field]: newArray };
      });
    } else {
      // Handle single fields
      setFormData(prev => ({ ...prev, [field]: value }));
      // Clear the suggestion when a user manually types in the field
      clearSuggestion(field);
    }
    
    // Track the change in Mixpanel
    Mixpanel.trackFeatureAction('GoalWizard', 'Updated Field', {
      field,
      step: currentStep.title,
      timestamp: new Date().toISOString()
    });
  };

  const clearSuggestion = (field) => {
    setSuggestions(prev => ({
      ...prev,
      [field]: Array.isArray(prev[field]) ? [] : ''
    }));
  };

  const generateSuggestions = async () => {
    setLoading(true);
    setError('');
    
    const field = currentStep.field;
    
    try {
      // Clear any existing suggestions first
      clearSuggestion(field);
      
      const token = localStorage.getItem('token');
      
      // Skip suggestion generation for non-data fields
      if (field === 'intro' || field === 'review') {
        logger.debug('Skipping suggestions for non-data field:', field);
        setLoading(false);
        return;
      }
      
      // Extract user data with proper fallbacks and structure
      const userData = {
        name: user?.name || '',
        sport: user?.sport || '',
        position: user?.sportPosition || '',
        gender: user?.gender || '',
        currentGoals: formData
      };
      
      logger.debug('Sending data to AI endpoint:', { field, userData });
      
      // Validate that we have a valid field before making the request
      if (!['mission', 'vision', 'processGoals'].includes(field)) {
        logger.error('Invalid field for suggestions:', field);
        setError('Cannot generate suggestions for this step.');
        setLoading(false);
        return;
      }
      
      // Map the field to the format expected by the backend
      const fieldMapping = {
        'mission': 'mission',
        'vision': 'longTermGoals',  // Updated to match backend expectation
        'processGoals': 'processGoals'
      };
      
      const formattedField = fieldMapping[field];
      
      if (!formattedField) {
        logger.error('Could not map field to expected format:', field);
        setError('Cannot generate suggestions for this field.');
        setLoading(false);
        return;
      }
      
      logger.debug('Using formatted field for API request:', formattedField);
      
      // Structure the request exactly as the backend expects it
      const requestData = {
        type: formattedField, // Using 'type' as expected by the updated backend
        userData
      };
      
      logger.debug('Request data structure:', requestData);
      logger.debug('Request data JSON:', JSON.stringify(requestData)); // Log the exact JSON being sent
      
      // Make a direct API call without caching
      logger.debug(`Making direct API call for ${field} (${formattedField})`);
      const response = await axios.post(
        `${API_URL}/api/ai/goal-suggestions`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // For array fields, ensure we have suggestions for all existing input fields
      if (Array.isArray(formData[field])) {
        const receivedSuggestions = response.data.suggestions || [];
        // Create an array with a suggestion for each input field
        const mappedSuggestions = formData[field].map((_, index) => 
          index < receivedSuggestions.length ? receivedSuggestions[index] : ''
        );
        
        setSuggestions(prev => ({
          ...prev,
          [field]: mappedSuggestions
        }));
      } else {
        // For single fields like mission
        setSuggestions(prev => ({
          ...prev,
          [field]: response.data.suggestions
        }));
      }
      
      Mixpanel.trackFeatureAction('GoalWizard', 'Generated Suggestions', {
        step: currentStep.title,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('Error generating suggestions:', error);
      
      // More detailed error handling based on the response
      if (error.response) {
        logger.debug('Error response data:', error.response.data);
        if (error.response.status === 400) {
          setError('Invalid request data. Please check your inputs and try again.');
        } else if (error.response.status === 401) {
          setError('Authentication error. Please log in again.');
        } else {
          setError(`Server error (${error.response.status}). Please try again later.`);
        }
      } else if (error.request) {
        // Request was made but no response received
        setError('No response from server. Please check your connection and try again.');
      } else {
        // Something else caused the error
        setError('Failed to generate suggestions. Please try again.');
      }
      
      // Provide fallback suggestions if API call fails
      const fallbackSuggestions = {
        mission: "To consistently push my limits and inspire others through my dedication, resilience, and sportsmanship in every aspect of my athletic journey.",
        vision: [
          "Improve my technical skills to become a starter on my team this season, measured by coach evaluations and increased playing time.",
          "Increase my physical conditioning to maintain peak performance throughout entire games, measured by fitness tests and recovery metrics.",
          "Develop leadership skills by mentoring younger teammates and taking on more responsibility during practices and games."
        ],
        processGoals: [
          "Complete 30 minutes of position-specific drills 4 times per week, focusing on technique and fundamentals.",
          "Follow a structured nutrition plan with 80% compliance, tracking meals and hydration daily.",
          "Practice visualization and mental rehearsal for 10 minutes before each practice and game to improve focus and confidence."
        ]
      };
      
      // Use fallback suggestions for the current field
      if (field in fallbackSuggestions) {
        setSuggestions(prev => ({
          ...prev,
          [field]: fallbackSuggestions[field]
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = (suggestion, index = null) => {
    const field = currentStep.field;
    
    if (index !== null) {
      // Apply to array field
      setFormData(prev => {
        const newArray = [...prev[field]];
        newArray[index] = suggestion;
        return { ...prev, [field]: newArray };
      });
      
      // Clear only this specific suggestion
      setSuggestions(prev => ({
        ...prev,
        [field]: prev[field].map((s, i) => (i === index ? '' : s))
      }));
    } else {
      // Apply to single field
      setFormData(prev => ({ ...prev, [field]: suggestion }));
      
      // Clear the single suggestion
      setSuggestions(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
      
      Mixpanel.trackFeatureAction('GoalWizard', 'Next Step', {
        fromStep: currentStep.title,
        toStep: steps[step + 1].title,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
      
      Mixpanel.trackFeatureAction('GoalWizard', 'Previous Step', {
        fromStep: currentStep.title,
        toStep: steps[step - 1].title,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // Track completion in Mixpanel
      Mixpanel.track('GoalWizard Completed', {
        feature: 'GoalWizard',
        action: 'Completed'
      });
      
      // Filter out empty values
      const filteredData = {
        mission: formData.mission.trim(),
        vision: formData.vision.filter(item => item && item.trim() !== ''),
        processGoals: formData.processGoals.filter(item => item && item.trim() !== '')
      };
      
      // Map the data to the format expected by the backend
      const mappedData = {
        mission: filteredData.mission || '',
        vision: filteredData.vision || [],
        principles: filteredData.processGoals || []
      };
      
      logger.debug('Saving MVP data to backend:', mappedData);
      
      // Get token for authentication
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You must be logged in to save changes');
        setLoading(false);
        return;
      }
      
      try {
        // Make API request to save the data
        logger.debug(`Making API request to ${API_URL}/api/users/mvp with data:`, JSON.stringify(mappedData));
        
        // Add a test request to verify the endpoint is working
        try {
          const testResponse = await axios.get(
            `${API_URL}/api/users/mvp`,
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );
          logger.debug('Test GET request to /api/users/mvp succeeded:', testResponse.data);
        } catch (testError) {
          logger.error('Test GET request to /api/users/mvp failed:', testError);
          logger.error('Error details:', testError.response?.data || 'No response data');
        }
        
        const response = await axios.put(
          `${API_URL}/api/users/mvp`, 
          mappedData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Handle successful response
        logger.debug('MVP save response:', response);
        
        if (response.status === 200) {
          logger.debug('Successfully saved MVP data to backend:', response.data);
          
          // Store in localStorage as backup
          localStorage.setItem('mvpData', JSON.stringify(mappedData));
          localStorage.setItem('mvpDataHasContent', 'true');
          
          // Call the onComplete callback with the filtered data
          if (onComplete) {
            onComplete(filteredData);
          }
          
          setSuccess(true);
          setLoading(false);
          
          // Close the wizard after a short delay to show success message
          setTimeout(() => {
            if (onClose) {
              onClose();
            }
          }, 1500);
        } else {
          logger.warn('Unexpected response status:', response.status);
          setError('Received an unexpected response from the server');
          setLoading(false);
        }
      } catch (error) {
        logger.error('Error saving MVP data:', error);
        logger.error('Error details:', error.response?.data || 'No response data');
        
        // Store in localStorage as fallback if API call fails
        localStorage.setItem('mvpData', JSON.stringify(mappedData));
        localStorage.setItem('mvpDataHasContent', 'true');
        logger.debug('Saved MVP data to localStorage as fallback');
        
        // Show a more user-friendly error message
        setError('Could not save to server, but your data has been saved locally');
        
        // Still call onComplete to allow the user to continue
        if (onComplete) {
          onComplete(filteredData);
        }
        
        setLoading(false);
      }
    } catch (error) {
      logger.error('Error saving MVP data:', error);
      logger.error('Error details:', error.response?.data || 'No response data');
      
      // Set appropriate error message
      if (error.response) {
        logger.error('Error response data:', error.response.data);
        logger.error('Error response status:', error.response.status);
        setError(`Server error: ${error.response.data.message || 'Unknown error'}`);
      } else if (error.request) {
        logger.error('Error request:', error.request);
        setError('No response from server. Please check your internet connection and try again.');
        
        // Save to localStorage as fallback
        const mappedData = {
          mission: formData.mission || '',
          vision: formData.vision.filter(item => item && item.trim() !== '') || [],
          principles: formData.processGoals.filter(item => item && item.trim() !== '') || []
        };
        localStorage.setItem('mvpData', JSON.stringify(mappedData));
        localStorage.setItem('mvpDataHasContent', 'true');
        
        // Call the onComplete callback with the filtered data
        if (onComplete) {
          onComplete({
            mission: formData.mission,
            vision: formData.vision.filter(item => item && item.trim() !== ''),
            processGoals: formData.processGoals.filter(item => item && item.trim() !== '')
          });
        }
        
        // Close the wizard
        if (onClose) {
          onClose();
        }
      } else {
        logger.error('Error message:', error.message);
        setError(`Error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderIntroduction = () => {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <Flag className="text-blue-600 mr-2" size={24} />
                <h4 className="font-bold">Mission</h4>
              </div>
              <p className="text-sm">Your purpose statement that defines why you do what you do and what you aim to achieve.</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <Flag className="text-blue-600 mr-2" size={24} />
                <h4 className="font-bold">Vision</h4>
              </div>
              <p className="text-sm">Your annual and season goals and aspirations that give direction to your mission.</p>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center mb-2">
                <Calendar className="text-blue-600 mr-2" size={24} />
                <h4 className="font-bold">Processes</h4>
              </div>
              <p className="text-sm">What daily/weekly actions will help you achieve your goals?</p>
            </div>
          </div>
          
          <h4 className="font-bold mb-2">Why You Need an MVP:</h4>
          <ul className="list-disc list-inside space-y-2 mb-4">
            <li>Provides clarity and purpose to your athletic journey</li>
            <li>Creates alignment between your daily actions and goals</li>        
            <li>Increases motivation and resilience during challenges</li>
            <li>Improves focus by eliminating distractions that don't serve your mission</li>
          </ul>
          
          <p className="text-sm italic">
            This wizard will guide you through creating each element of your MVP, with AI-powered suggestions to help you along the way.
          </p>
        </div>
      </div>
    );
  };

  const renderInputField = () => {
    const field = currentStep.field;
    
    // For intro and review steps, just show the content
    if (field === 'intro') {
      return renderIntroduction();
    }
    
    if (field === 'review') {
      return (
        <div className="space-y-6">
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg font-bold mb-2">Mission Statement</h3>
              <div className="bg-base-200 p-4 rounded-lg">
                <p className="whitespace-pre-wrap">{formData.mission || 'Not defined'}</p>
              </div>
            </div>
          </div>
          
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg font-bold mb-2">Vision Goals</h3>
              {formData.vision.filter(goal => goal.trim()).length > 0 ? (
                <ul className="list-disc list-inside space-y-2">
                  {formData.vision.map((goal, index) => (
                    goal.trim() ? (
                      <li key={index} className="bg-base-200 p-3 rounded-lg">
                        {goal}
                      </li>
                    ) : null
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic">No vision goals defined</p>
              )}
            </div>
          </div>
          
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body">
              <h3 className="card-title text-lg font-bold mb-2">Process Goals</h3>
              {formData.processGoals.filter(goal => goal.trim()).length > 0 ? (
                <ul className="list-disc list-inside space-y-2">
                  {formData.processGoals.map((goal, index) => (
                    goal.trim() ? (
                      <li key={index} className="bg-base-200 p-3 rounded-lg">
                        {goal}
                      </li>
                    ) : null
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic">No process goals defined</p>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    // For mission (single input field)
    if (field === 'mission') {
      return (
        <div>
          <textarea
            value={formData[field] || ''}
            onChange={(e) => handleInputChange(field, e)}
            className="textarea textarea-bordered w-full min-h-[120px]"
            placeholder="Enter your mission statement..."
          />
          
          {/* AI Suggestion section */}
          <GenerateButton />
          
          {suggestions[field] && (
            <>
              <div className="card bg-blue-50 p-4 mb-2 rounded-lg">
                <p className="text-blue-700 font-medium">{suggestions[field]}</p>
              </div>
              <button
                onClick={() => applySuggestion(suggestions[field])}
                className="btn btn-sm btn-ghost w-full"
              >
                Use Suggestion
              </button>
            </>
          )}
        </div>
      );
    }
    
    // For array fields (vision, processGoals)
    return (
      <div className="space-y-2">
        {(formData[field] || ['', '', '']).map((value, index) => (
          <div key={index} className="card bg-base-100 shadow-sm">
            <div className="p-2.5">
              <div className="flex justify-between items-start gap-2 mb-1">
                <h3 className="text-sm font-medium whitespace-nowrap">
                  {index === 0 ? 'Main Goal' : `Goal ${index + 1}`}
                </h3>
                {index > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newValues = [...formData[field]];
                      newValues.splice(index, 1);
                      handleInputChange(field, newValues);
                    }}
                    className="btn btn-ghost btn-xs btn-square text-error p-0 h-5 w-5 min-h-0 flex-shrink-0 mt-0.5"
                    style={{ marginTop: '-0.25rem' }}
                    aria-label="Remove goal"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              
              <textarea
                value={value}
                onChange={(e) => handleInputChange(field, e, index)}
                className="textarea textarea-bordered w-full min-h-[70px] text-sm py-1.5 px-3 mt-1"
                placeholder={index === 0 ? 'Enter your main goal...' : `Enter goal ${index + 1}...`}
              />
              
              {/* Show AI suggestion if available for this index */}
              {suggestions[field]?.[index] && (
                <div className="mt-1.5">
                  <div className="bg-blue-50 p-2 rounded border border-blue-100">
                    <p className="text-blue-700 text-sm">{suggestions[field][index]}</p>
                  </div>
                  <button
                    onClick={() => applySuggestion(suggestions[field][index], index)}
                    className="btn btn-xs btn-ghost w-full mt-1 text-xs h-7 min-h-7"
                  >
                    Use Suggestion
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        
        {/* Add button for new goal (max 5) */}
        {formData[field] && formData[field].length < 5 && (
          <button
            onClick={() => {
              const newValues = [...(formData[field] || [])];
              newValues.push('');
              handleInputChange(field, newValues);
            }}
            className="btn btn-outline btn-sm w-full"
          >
            Add Goal
          </button>
        )}
        
        <GenerateButton />
      </div>
    );
  };

  // Create a reusable AI Generate button component
  const GenerateButton = () => {
    return (
      <div className="flex justify-end mt-4">
        <button
          onClick={generateSuggestions}
          disabled={loading}
          className="btn btn-primary btn-xs"
        >
          {loading ? (
            <>
              <span className="loading loading-spinner loading-xs"></span>
              Generating...
            </>
          ) : (
            <>
              Generate
            </>
          )}
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center ${isOpen ? 'visible' : 'invisible'}`}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="sticky top-0 z-10 flex justify-between items-center border-b p-4 bg-white">
          <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">Goal Setting Wizard</h2>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost ml-4"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4 md:p-6">
          {/* Simple progress bar instead of circles */}
          <div className="mb-6 md:mb-8">
            <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="absolute top-0 left-0 h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${(step / (steps.length - 1)) * 100}%` }}
              ></div>
            </div>
          </div>
          
          {/* Step content */}
          <div className="mb-6 md:mb-8">
            {/* Add heading for each step */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold">
                {currentStep.field === 'intro' && "What Is MVP?"}
                {currentStep.field === 'mission' && "Your Mission Statement"}
                {currentStep.field === 'vision' && "Your Vision"}
                {currentStep.field === 'processGoals' && "Your Process Goals"}
                {currentStep.field === 'review' && "Review Your Goals"}
              </h2>
              <p className="text-sm text-gray-600">
                {currentStep.description}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {currentStep.instruction}
              </p>
            </div>
            
            {error && (
              <div className="text-center mb-4">
                <p className="text-red-500 font-medium">{error}</p>
              </div>
            )}
            
            {success && (
              <div className="text-center mb-4">
                <p className="text-green-500 font-medium">Your MVP data has been saved successfully!</p>
              </div>
            )}
            
            {renderInputField()}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex flex-col-reverse md:flex-row justify-between gap-3 mt-6">
            <button
              onClick={handleBack}
              disabled={step === 0}
              className="btn btn-outline btn-neutral w-full md:w-auto border-2 border-blue-500"
            >
              Back
            </button>
            
            {step < steps.length - 1 ? (
              <button
                onClick={handleNext}
                className="btn btn-primary w-full md:w-auto"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="btn btn-primary w-full md:w-auto"
              >
                {loading ? (
                  <>
                    <span className="loading loading-spinner loading-xs"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    Complete
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalWizard;